/**
 * usePracticeSession.ts — Extracted from PracticeView.vue Phase 2
 *
 * The /legacy screen's recorder wiring: the FSM reactions to VAD events, toggleRecord(),
 * setVoiceSensitivity(), setSilencePatience(), and keyboard handler setup. The recorder itself
 * (RecorderService creation, base callbacks, upload dialog) is the shared useRecorder.
 *
 * State remains in PracticeView's reactive object; this composable operates
 * on Ref-wrapped state properties passed via the deps interface.
 */
import type { AliyahType, SentenceType } from 'types/types'
import { ComputedRef, DeepReadonly, Ref } from 'vue'
import { VueLogger } from 'vue-logger-plugin'

import {
  INTERSEGMENT_PAUSE,
  INTERWORD_PAUSE,
  NEXT_KEY,
  practiceTextSegmentId,
  PREV_KEY,
  SING_NOW_POPOVER_SHOW,
} from '@/constants/practiceConstants'
import type { RecorderServiceOptions } from '@/util/Recording'
import type { TransformationType } from '@/util/Transformations'
import { allTransformations } from '@/util/Transformations'

import { ReadOrder, studentParticipates } from './usePracticeFSM'
import { type RecorderDeps, type RecorderState, useRecorder } from './useRecorder'

// {{{ interface
export interface PracticeSessionDeps extends RecorderState {
  // State refs (from PracticeView's toRefs — Session reads and writes these)
  voiceSensitivity: Ref<number>
  silencePatience: Ref<number>
  recordMe: Ref<boolean>
  silenceStartedAt: Ref<Date | null>
  readOrder: Ref<ReadOrder>
  manualSegmentJump: Ref<boolean>
  focusSentenceIndex: Ref<number | null>
  focusAnnotatedSentence: Ref<unknown | null>
  popoverVisible: Ref<boolean>
  aliyah: Ref<AliyahType | null>
  transformation: Ref<TransformationType | null>

  // From usePracticeFSM
  fsmState: Ref<number>
  fsmHistory: Ref<string[]>
  waitForVoiceStart: Ref<boolean>
  waitForVoiceEnd: Ref<boolean>
  manualForwardKeyPressed: Ref<boolean>
  finalizerHandler: Ref<NodeJS.Timeout | null>
  popoverShowStartNow: Ref<boolean>
  recordButtonPressed: Ref<boolean>
  driveState: () => void
  initializeRecordingState: () => void

  // From useFollower
  currentSegmentIndex: Ref<number>
  currentSentenceIndex: Ref<number>
  followerOptions: DeepReadonly<RecorderServiceOptions>
  setCurrentSegment: (sentenceIdx: number, segmentIdx: number) => void

  // From useAudioPlayer
  playing: Ref<boolean>
  pause: () => void

  // Computed refs (from PracticeView)
  sentence: ComputedRef<SentenceType | null>
  isSegmented: ComputedRef<boolean>
  isRecording: ComputedRef<boolean>

  // Callbacks (defined in PracticeView, hoisted functions)
  advanceToSentence: (increment: number, absolute?: boolean) => Promise<void>
  hasRecordingBySentenceIndex: (idx: number) => boolean
  closePracticeOverlay: () => void
  closePopover: () => void
  practiceUpload: RecorderDeps['practiceUpload']

  // Util
  isProduction: () => boolean
}
// }}}

export function usePracticeSession(logger: VueLogger, deps: PracticeSessionDeps) {
  // {{{ recorder
  /** Returns true if voice events should be ignored (TEACHER_ONLY mode or responsive sentence). */
  function shouldIgnoreVoiceEvent(caller: string): boolean {
    if (!deps.sentence.value) {
      logger.error(`unexpected null sentence.value in ${caller}`)
      return true
    }
    return !studentParticipates(deps.readOrder.value) || deps.sentence.value.isResponsive
  }

  const recorder = useRecorder(logger, {
    ignoreVoiceEvent: shouldIgnoreVoiceEvent,
    keepRecording: () => deps.recordMe.value,
    onRecordingStarted: () => deps.driveState(),
    onRecordingStarting: () => {
      // show the "Sing Now!" flag when the student goes first
      if (deps.readOrder.value === ReadOrder.STUDENT_ONLY || deps.readOrder.value === ReadOrder.STUDENT_TEACHER) {
        deps.popoverShowStartNow.value = true
        deps.popoverVisible.value = true
        setTimeout(() => {
          deps.popoverShowStartNow.value = false
          deps.popoverVisible.value = false
        }, SING_NOW_POPOVER_SHOW)
      }
    },
    onVoiceStart: () => {
      deps.followerOptions.onVoiceStart?.(practiceTextSegmentId(deps.currentSentenceIndex.value, deps.currentSegmentIndex.value))
      if (deps.waitForVoiceStart.value) {
        deps.fsmState.value = 6
        deps.driveState()
      }
    },
    onVoiceStop: () => {
      deps.followerOptions.onVoiceStop?.()
      if (deps.waitForVoiceEnd.value) {
        // Duplicated with Recording.vue
        let msSinceSilenceStarted = 0
        if (deps.silenceStartedAt.value) {
          msSinceSilenceStarted = new Date().getTime() - deps.silenceStartedAt.value.getTime()
        }
        deps.silenceStartedAt.value = null // reset for next word

        if (deps.finalizerHandler.value) {
          clearTimeout(deps.finalizerHandler.value)
        }
        deps.finalizerHandler.value = setTimeout(
          () => {
            deps.finalizerHandler.value = null
            deps.fsmState.value = 10
            deps.driveState()
          },
          deps.isSegmented.value
            ? Math.max(0, INTERSEGMENT_PAUSE - msSinceSilenceStarted)
            : Math.max(0, INTERWORD_PAUSE - msSinceSilenceStarted),
        )
      }
    },
    options: {
      ...deps.followerOptions,
      onStartSilence: () => {
        deps.silenceStartedAt.value = new Date()
      },
    },
    practiceUpload: deps.practiceUpload,
    state: deps,
    vadSettings: () => ({ sensitivity: deps.voiceSensitivity.value, silencePatience: deps.silencePatience.value }),
  })

  /**
   * CHAIN 5: RecorderService → FSM callbacks
   * Must be called from onMounted in the parent.
   * @invariant VAD callbacks (onVoiceStart, onVoiceStop) only drive FSM when FSM is in a waiting
   *   state (waitForVoiceStart or waitForVoiceEnd). In TEACHER_ONLY mode and for responsive
   *   sentences, voice events are ignored entirely.
   * @sideeffect Creates RecorderService instance and writes to deps.recorderService ref;
   *   sets initial transformation
   */
  function initRecorderService() {
    deps.setCurrentSegment(-1, -1)
    deps.transformation.value = allTransformations(!!deps.aliyah.value)[0] as TransformationType
    recorder.ensureRecorderService()
  }
  // }}}

  // {{{ setupKeyboardHandler
  /**
   * Must be called from onMounted in the parent.
   * @sideeffect Registers global keydown listener
   */
  function setupKeyboardHandler(): () => void {
    const handler = (e: KeyboardEvent) => {
      // during recording, this interacts with the FSM
      if (deps.isRecording.value) {
        if (e.key === NEXT_KEY && deps.manualSegmentJump.value) {
          deps.manualForwardKeyPressed.value = true
          // only drive the state if we're in state 16; otherwise the user interrupted the flow and we're at risk starting a
          // parallel FSM flow
          if (deps.fsmState.value === 16) {
            deps.driveState()
          }
          return
        }

        // in development mode we can fake voiceStart and voiceEnd events
        if (!deps.isProduction()) {
          if (e.key === 's' && deps.waitForVoiceStart.value) {
            deps.fsmState.value = 6
            deps.driveState()
          } else if (e.key === 'e' && deps.waitForVoiceEnd.value) {
            deps.fsmState.value = 10
            deps.driveState()
          }
        }
      }

      // during NOT-recording this navigates between sentences
      if (deps.focusSentenceIndex.value === null) {
        return
      }

      if (e.key === NEXT_KEY || e.key === 'ArrowRight') {
        if (deps.hasRecordingBySentenceIndex(deps.focusSentenceIndex.value + 1)) {
          deps.advanceToSentence(1)
        }
      } else if (e.key === PREV_KEY || e.key === 'ArrowLeft') {
        if (deps.hasRecordingBySentenceIndex(deps.focusSentenceIndex.value - 1)) {
          deps.advanceToSentence(-1)
        }
      } else if (e.key === 'Escape') {
        if (deps.popoverVisible.value) {
          deps.closePopover()
          return
        }

        if (deps.focusAnnotatedSentence.value) {
          deps.closePracticeOverlay()
        }
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }
  // }}}

  // {{{ toggleRecord
  /**
   * @postcondition Recording toggled: if was recording, stops and resets FSM; if was not recording,
   *   starts recording with VAD (or drives FSM directly for TEACHER_ONLY mode)
   * @sideeffect When stopping: calls recorderService.stopRecording(), clears finalizerHandler,
   *   resets background sampling, calls initializeRecordingState()
   * @sideeffect When starting: enables VAD, calls recorderService.startRecording() or drives FSM
   * @precondition recorderService is initialized (from initRecorderService)
   */
  function toggleRecord(): void {
    logger.debug('toggleRecord')
    // stop the audio that's playing as part of practicing
    if (deps.playing.value) {
      deps.pause()
    }

    if (deps.isRecording.value) {
      logger.debug('toggleRecord: isRecording.value true')

      if (deps.finalizerHandler.value) {
        clearTimeout(deps.finalizerHandler.value)
        deps.finalizerHandler.value = null
      }

      if (!deps.recorderService.value) {
        logger.error('unexpected null recorderService in toggleRecord')
        return
      }

      recorder.stopRecorder()
      deps.popoverShowStartNow.value = false
      deps.recordButtonPressed.value = false
      deps.initializeRecordingState()
    } else if (!deps.recorderService.value?.options) {
      logger.error('unexpected null recorderService.options')
    } else {
      deps.initializeRecordingState()
      deps.fsmHistory.value = []

      // in all but Teacher-only mode we'll need the VAD
      if (studentParticipates(deps.readOrder.value)) {
        recorder.startRecorder()
      } else {
        deps.fsmState.value = 0
        deps.driveState()
      }
    }
  }
  // }}}

  // {{{ setVoiceSensitivity
  /**
   * @postcondition VAD sensitivity updated in both state and recorderService
   * @sideeffect Mutates voiceSensitivity ref; calls recorderService.setSensitivity()
   */
  function setVoiceSensitivity(value: number) {
    deps.voiceSensitivity.value = value
    if (deps.recorderService.value) {
      deps.recorderService.value.setSensitivity(value)
    }
  }
  // }}}

  // {{{ setSilencePatience
  /**
   * @postcondition Silence patience updated in both state and recorderService
   * @sideeffect Mutates silencePatience ref; calls recorderService.setSlowdownEwmaMultiplier()
   */
  function setSilencePatience(value: number) {
    deps.silencePatience.value = value
    if (deps.recorderService.value) {
      deps.recorderService.value.setSlowdownEwmaMultiplier(deps.silencePatience.value)
    }
  }
  // }}}

  return {
    discardRecording: recorder.discardRecording,
    initRecorderService,
    saveRecording: recorder.saveRecording,
    setSilencePatience,
    setVoiceSensitivity,
    setupKeyboardHandler,
    shareRecording: recorder.shareRecording,
    toggleRecord,
  }
}
